import { memo, useEffect, useState, useRef, useMemo } from "react"
import { Stack, Text, Button, Container, Separator, Drawer, Portal, CloseButton, Input, Box, Flex } from "@chakra-ui/react"
import { useCart } from "contexts/cart"
import { formatCurrency } from "@/utilities/number"
import VoucherService from "@/services/voucher"
import _ from "lodash"
const service = new VoucherService();

const toSafeNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
}

const formatPercent = (value) => {
    const num = toSafeNumber(value, 0);
    return `${new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 2 }).format(num)}%`;
}

const getVoucherDiscountText = (voucher) => {
    if (!voucher) return "";
    if (voucher.type === "fixed") {
        return `Giảm ${formatCurrency(toSafeNumber(voucher.value, 0))}đ`;
    }
    const maxDiscount = toSafeNumber(voucher.max_discount_amount, 0);
    if (maxDiscount > 0) {
        return `Giảm ${formatPercent(voucher.value)} (tối đa ${formatCurrency(maxDiscount)}đ)`;
    }
    return `Giảm ${formatPercent(voucher.value)}`;
}

const estimateVoucherAmount = (voucher, subAmount) => {
    if (!voucher) return 0;
    const subtotal = Math.max(0, toSafeNumber(subAmount, 0));
    if (subtotal <= 0) return 0;

    if (voucher.type === "fixed") {
        return Math.min(subtotal, Math.max(0, toSafeNumber(voucher.value, 0)));
    }

    let amount = subtotal * toSafeNumber(voucher.value, 0) / 100;
    const maxDiscount = toSafeNumber(voucher.max_discount_amount, 0);
    if (maxDiscount > 0) {
        amount = Math.min(amount, maxDiscount);
    }
    return Math.min(subtotal, Math.max(0, amount));
}

const VoucherInput = () => {
    const [open, setOpen] = useState(false)
    const ref = useRef(null);
    const cart = useCart();

    const [voucher, setVoucher] = useState();
    const [voucherCode, setVoucherCode] = useState("");
    const [canApply, setCanApply] = useState(false);
    const [error, setError] = useState();
    const [publicVouchers, setPublicVouchers] = useState([]);
    const [isLoadingPublic, setIsLoadingPublic] = useState(false);
    const subAmount = toSafeNumber(cart?.cart?.sub_amount, 0);
    const appliedVoucherCode = cart?.cart?.voucher_code || "";
    const appliedVoucherAmount = toSafeNumber(cart?.cart?.voucher_amount, 0);

    const loadPublicVouchers = async (info) => {
        setIsLoadingPublic(true);
        try {
            const data = await service.publicList({ cart_id: cart.cart?.id, info });
            setPublicVouchers(Array.isArray(data) ? data : []);
        } catch (_) {
            setPublicVouchers([]);
        }
        setIsLoadingPublic(false);
    }

    const onVoucherChange = (e) => {
        setVoucherCode(e.target.value);
        searchDelay(e.target.value)
    }
    useEffect(() => {
        setVoucher(null);
        setError(null);
        setCanApply(false);
        if (open) {
            const code = appliedVoucherCode || "";
            setVoucherCode(code);
            if (code) {
                loadVoucher(code);
            }
            loadPublicVouchers("");
        } else {
            setVoucherCode("");
        }
    }, [open, appliedVoucherCode])


    const loadVoucher = async (code) => {
        setVoucher(null)
        setError(null)
        if (!code || !code.trim()) {
            setCanApply(false);
            return;
        }
        try {
            var data = await service.check(code, cart.cart?.id);
            if (data.error) {
                setError(data.error.message);
                setCanApply(false);
            } else {
                setVoucher(data.voucher)
                setError(null);
                setCanApply(true);
            }
        } catch (e) {
            setError(e?.message || "Không thể kiểm tra mã voucher");
            setCanApply(false);
        }
    }
    const onSearch = (text) => {
        loadVoucher(text);
        loadPublicVouchers(text);
    }

    const searchDelay = useMemo(() => _.debounce(onSearch, 500), []);
    const sortedPublicVouchers = useMemo(() => {
        const cloned = [...publicVouchers];
        cloned.sort((a, b) => {
            if (a?.can_apply !== b?.can_apply) {
                return a?.can_apply ? -1 : 1;
            }
            const discountA = estimateVoucherAmount(a?.voucher, subAmount);
            const discountB = estimateVoucherAmount(b?.voucher, subAmount);
            return discountB - discountA;
        });
        return cloned;
    }, [publicVouchers, subAmount]);

    useEffect(() => {
        return () => {
            searchDelay.cancel();
        }
    }, [searchDelay]);

    const applyVoucherCode = async (code) => {
        if (!code) return;
        try {
            await cart.setVoucher(code)
            setOpen(false);
        } catch (e) {
            setError(e?.message ?? "Không thể áp dụng mã voucher");
        }
    }

    const applyVoucher = async () => {
        if (!voucher?.code) {
            setCanApply(false);
            return;
        }
        await applyVoucherCode(voucher.code)
    }

    const removeAppliedVoucher = async () => {
        await cart.removeVoucher();
        setVoucher(null);
        setVoucherCode("");
        setCanApply(false);
        setError(null);
    }





    return (
        <>
            {appliedVoucherCode ?

                <Box borderWidth="1px" borderColor="border" borderRadius="md" bg="bg.subtle" px={3} py={2} width="100%">
                    <Flex alignItems="center" justifyContent="space-between" gap={3}>
                        <Stack gap={0} minWidth={0}>
                            <Text fontSize="sm" color="fg.muted">Đang áp dụng mã</Text>
                            <Text fontWeight="semibold" truncate>{appliedVoucherCode}</Text>
                        </Stack>
                        <Stack gap={0} alignItems="flex-end" flexShrink={0}>
                            <Text fontSize="sm" color="fg.muted">Giảm</Text>
                            <Text fontWeight="semibold" color="fg">{formatCurrency(appliedVoucherAmount)}đ</Text>
                            <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>Đổi mã</Button>
                        </Stack>
                    </Flex>
                </Box>

                :
                <Button variant={"ghost"} onClick={e => setOpen(true)} style={{ padding: 0 }}>
                    Mã khuyến mãi
                </Button>
            }


            <Portal>


                <Drawer.Root open={open} onOpenChange={(e) => setOpen(e.open)} placement={"bottom"} initialFocusEl={() => ref.current} closeOnInteractOutside={false}>
                    <Drawer.Backdrop />
                    <Drawer.Positioner>
                        <Container maxWidth={"lg"} style={{ padding: 0 }}>
                            <Drawer.Content >


                                <Drawer.Header>
                                    <Drawer.Title>Khuyến Mãi</Drawer.Title>
                                </Drawer.Header>
                                <Drawer.Body>
                                    <Stack>
                                        <Input ref={ref} placeholder="Nhập mã khuyến mãi" value={voucherCode} onChange={onVoucherChange} />
                                        <div>
                                            {voucher &&
                                                <>
                                                    <Text>{getVoucherDiscountText(voucher)}</Text>
                                                    <Text fontSize={"sm"} color={"fg.muted"}>Đơn tối thiểu: {formatCurrency(toSafeNumber(voucher?.min_order_amount, 0))}đ</Text>
                                                    <Text fontSize={"sm"} color={"fg"}>Ước tính giảm hiện tại: {formatCurrency(estimateVoucherAmount(voucher, subAmount))}đ</Text>

                                                </>
                                            }
                                        </div>
                                        <div>
                                            {error &&
                                                <Text color={"fg"}>{error}</Text>
                                            }

                                        </div>

                                        <Separator />

                                        <Stack gap={3}>
                                            <Text fontWeight={"medium"}>Ưu đãi dành cho bạn</Text>
                                            {isLoadingPublic && <Text color={"fg.muted"}>Đang tải danh sách voucher...</Text>}

                                            {!isLoadingPublic && sortedPublicVouchers.map((item) => (
                                                <Box
                                                    key={item?.voucher?.id || item?.voucher?.code}
                                                    position={"relative"}
                                                    borderWidth={"1px"}
                                                    borderStyle={"dashed"}
                                                    borderColor={"border"}
                                                    borderRadius={"lg"}
                                                    bg={"bg.subtle"}
                                                    p={2}
                                                    _before={{
                                                        content: '""',
                                                        position: "absolute",
                                                        left: "-8px",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        width: "16px",
                                                        height: "16px",
                                                        borderRadius: "full",
                                                        bg: "bg",
                                                        borderWidth: "1px",
                                                        borderColor: "border",
                                                    }}
                                                    _after={{
                                                        content: '""',
                                                        position: "absolute",
                                                        right: "-8px",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        width: "16px",
                                                        height: "16px",
                                                        borderRadius: "full",
                                                        bg: "bg",
                                                        borderWidth: "1px",
                                                        borderColor: "border",
                                                    }}
                                                >
                                                    <Flex justifyContent={"space-between"} alignItems={"center"} gap={2}>
                                                        <Stack gap={0} flex={1}>
                                                            <Flex alignItems={"center"} gap={2}>
                                                                <Text fontWeight={"semibold"} fontSize={"sm"}>{item?.voucher?.code}</Text>
                                                                <Text fontSize={"xs"} color={"fg.muted"}>
                                                                    {item?.can_apply ? "Áp dụng được" : "Chưa đủ điều kiện"}
                                                                </Text>
                                                            </Flex>
                                                            <Text fontSize={"xs"} color={"fg.muted"} lineClamp={1}>{item?.voucher?.description || "Không có mô tả"}</Text>
                                                            <Text fontSize={"sm"}>{getVoucherDiscountText(item?.voucher)}</Text>
                                                            <Text fontSize={"xs"} color={"fg.muted"}>
                                                                Tối thiểu {formatCurrency(toSafeNumber(item?.voucher?.min_order_amount, 0))}đ • Ước tính giảm {formatCurrency(estimateVoucherAmount(item?.voucher, subAmount))}đ
                                                            </Text>
                                                            {!item?.can_apply && (
                                                                <Text fontSize={"xs"} color={"fg"} lineClamp={1}>{item?.error?.message || "Chưa đủ điều kiện áp dụng"}</Text>
                                                            )}
                                                        </Stack>
                                                        <Flex
                                                            borderLeftWidth={"1px"}
                                                            borderStyle={"dashed"}
                                                            borderColor={"border"}
                                                            pl={2}
                                                            alignItems={"center"}
                                                        >
                                                            <Button
                                                                size={"xs"}
                                                                variant={item?.can_apply ? "solid" : "subtle"}
                                                                disabled={!item?.can_apply}
                                                                onClick={() => applyVoucherCode(item?.voucher?.code)}
                                                            >
                                                                {item?.can_apply ? "Áp dụng ngay" : "Chưa áp dụng"}
                                                            </Button>
                                                        </Flex>
                                                    </Flex>
                                                </Box>
                                            ))}

                                            {!isLoadingPublic && publicVouchers.length === 0 && (
                                                <Text color={"fg.muted"}>Hiện chưa có ưu đãi phù hợp</Text>
                                            )}
                                        </Stack>
                                    </Stack>



                                </Drawer.Body>
                                <Drawer.Footer>
                                    {appliedVoucherCode && (
                                        <Button variant="ghost" onClick={removeAppliedVoucher}>Xoá mã đang áp dụng</Button>
                                    )}
                                    <Button variant="outline" onClick={e => { setOpen(false) }}>Huỷ</Button>
                                    <Button disabled={!canApply} onClick={applyVoucher}>Áp dụng</Button>
                                </Drawer.Footer>
                                <Drawer.CloseTrigger asChild>
                                    <CloseButton size="sm" />
                                </Drawer.CloseTrigger>

                            </Drawer.Content>
                        </Container>
                    </Drawer.Positioner>
                </Drawer.Root>

            </Portal>
        </>
    )
}

export default memo(VoucherInput)